import React, { useState, useEffect, useContext } from "react";
import shopStyles from "./Shop.module.css";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Accordion from "react-bootstrap/Accordion";
import "react-range-slider-input/dist/style.css";
import Card from "react-bootstrap/Card";
import Image from "react-bootstrap/Image";
import Form from "react-bootstrap/Form";
import Offcanvas from "react-bootstrap/Offcanvas";
import { ProductContext } from "../../context/ProductContext";
import { ProductMoreContext } from "../../context/ProductMoreContext";
import Spinner from "react-bootstrap/Spinner";
import axios from "axios";

const Shop = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [visibleProducts, setVisibleProducts] = useState(10);
  const productsPerPage = 10;

  const [categories, setCategories] = useState([]);
  const [categoryBtn, setCategoryBtn] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [sortBy, setSortBy] = useState("latest");

  // Get search query from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get("search") || "";
  const [isSearchMode, setIsSearchMode] = useState(!!searchQuery);

  const pathSegments = location.pathname.split("/").filter(Boolean);

  let currentPath = "";
  const crumbs = pathSegments.map((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === pathSegments.length - 1;

    return (
      <span key={currentPath}>
        {isLast ? (
          <span>{segment.charAt(0).toUpperCase() + segment.slice(1)}</span>
        ) : (
          <>
            <Link to={currentPath}>{segment}</Link>
            <span> / </span>
          </>
        )}
      </span>
    );
  });

  const getProductImage = (imageLink) => {
    // Handle array of images - return first image
    if (Array.isArray(imageLink) && imageLink.length > 0) {
      return imageLink[0];
    }

    // Handle single image string
    if (typeof imageLink === "string" && imageLink.trim()) {
      return imageLink;
    }

    // Fallback placeholder
    return "https://via.placeholder.com/300";
  };

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [resultPageNum, setResultPageNum] = useState(1);

  const nextButtons = Array.from({ length: 5 }, (_, index) => index).filter(
    (value) => value > 0
  );

  const { productMore, setProductMore } = useContext(ProductMoreContext);
  const { setProductID } = useContext(ProductContext);

  // Search functionality
  const performSearch = async (query) => {
    if (!query || !query.trim()) {
      setSearchResults([]);
      setIsSearchMode(false);
      return;
    }

    try {
      console.log("Performing search for:", query);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}product/search?`,
        {
          params: { q: query.trim() },
        }
      );

      const results = response.data || [];
      console.log("Search results:", results);
      setSearchResults(results);
      setIsSearchMode(true);
      setVisibleProducts(10); // Reset visible products when searching
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
      setIsSearchMode(true); // Still show search mode even with error
    }
  };

  // Effect to handle search when component mounts or search query changes
  useEffect(() => {
    if (searchQuery) {
      performSearch(searchQuery);
    } else {
      setSearchResults([]);
      setIsSearchMode(false);
    }
  }, [searchQuery]);

  // ---------- Helpers: normalize + extraction ----------

  const toStr = (v) => (v === null || v === undefined ? "" : String(v));
  const norm = (v) => toStr(v).trim();
  const normLower = (v) => norm(v).toLowerCase();

  const ensureArray = (val) => {
    if (Array.isArray(val)) return val;
    if (val === null || val === undefined) return [];
    return [val];
  };

  // Extract every plausible category value from a product as strings (both raw and lowercased)
  const extractProductCategoryValues = (product) => {
    const candidates = [
      product.categoryId,
      product.category,
      product.Category,
      product.cat_id,
      product.category_id,
      product.catId,
      product.categoryName,
    ];

    const arrayCandidates = [product.categories, product.categoryIds];

    const values = new Set();

    candidates.forEach((v) => {
      if (v !== undefined && v !== null && v !== "") {
        values.add(norm(v));
        values.add(normLower(v));
      }
    });

    arrayCandidates.forEach((arr) => {
      ensureArray(arr).forEach((v) => {
        if (v !== undefined && v !== null && v !== "") {
          values.add(norm(v));
          values.add(normLower(v));
        }
      });
    });

    return values; // Set of strings
  };

  // Build a lookup map for categories by _id
  const categoryById = (id) => categories.find((c) => c._id === id);

  // Build category tree
  const buildCategoryTree = (categories) => {
    const categoryMap = {};
    const rootCategories = [];

    categories.forEach((category) => {
      categoryMap[category._id] = { ...category, children: [] };
    });

    categories.forEach((category) => {
      if (category.parentId && categoryMap[category.parentId]) {
        categoryMap[category.parentId].children.push(categoryMap[category._id]);
      } else {
        rootCategories.push(categoryMap[category._id]);
      }
    });

    return rootCategories;
  };

  // Collect ALL matching keys for a category and all its descendants
  const collectCategoryKeysRecursively = (categoryNode) => {
    const keys = new Set();

    const pushKey = (v) => {
      if (v !== undefined && v !== null && v !== "") {
        keys.add(norm(v));
        keys.add(normLower(v));
      }
    };

    const visit = (node) => {
      // Common fields we might have on categories
      pushKey(node._id);
      pushKey(node.id);
      pushKey(node.Name);
      pushKey(node.name);
      pushKey(node.slug);
      pushKey(node.Slug);
      pushKey(node.code);
      pushKey(node.Code);
      pushKey(node.key);
      pushKey(node.Key);

      if (node.children && node.children.length > 0) {
        node.children.forEach(visit);
      }
    };

    visit(categoryNode);
    return keys; // Set of normalized string keys
  };

  // Recursive component to render nested categories with text-based expansion
  const CategoryItem = ({ category, level = 0 }) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category._id);
    const isSelected = selectedCategory === category._id;

    const toggleExpanded = (e) => {
      e.stopPropagation();
      const newExpanded = new Set(expandedCategories);
      if (isExpanded) {
        newExpanded.delete(category._id);
      } else {
        newExpanded.add(category._id);
      }
      setExpandedCategories(newExpanded);
    };

    const handleCategoryClick = () => {
      // Clear search mode when selecting a category
      setIsSearchMode(false);
      navigate("/shop"); // Remove search query from URL

      setSelectedCategory(category._id);

      // Build a robust match key set for this category + all descendants
      const categoryKeys = collectCategoryKeysRecursively(category);

      // Simple version: match by any overlapping key
      const filtered = products.filter((product, index) => {
        const prodKeys = extractProductCategoryValues(product); // Set of strings
        // intersection check
        for (const k of prodKeys) {
          if (categoryKeys.has(k)) return true;
        }
        return false;
      });

      console.log(
        `Filter "${category.Name}" matched ${filtered.length} products. Keys:`,
        Array.from(categoryKeys).slice(0, 10),
        categoryKeys.size > 10 ? "..." : ""
      );

      setFilteredProducts(filtered);
      setVisibleProducts(10); // Reset visible products when filtering
      handleClose(); // Close mobile menu after selection

      console.log(`Selected category: ${category.Name} (${category._id})`);
    };

    return (
      <div style={{ marginLeft: `${level * 16}px` }}>
        <div className="d-flex align-items-center">
          {/* Category text/link */}
          <span
            onClick={handleCategoryClick}
            className={`${isSelected ? "fw-bold text-primary" : "text-dark"} 
              ${hasChildren && !isSelected ? "text-decoration-none" : ""}`}
            style={{
              cursor: "pointer",
              flex: 1,
              fontSize: "14px",
              lineHeight: "1.4",
              padding: "4px 0",
              color: isSelected
                ? "#0d6efd"
                : hasChildren
                ? "#495057"
                : "#6c757d",
            }}
          >
            {category.Name}
            {hasChildren && (
              <span className="text-muted ms-1" style={{ fontSize: "12px" }}>
                ({category.children.length})
              </span>
            )}
          </span>

          {/* Expand/collapse icon for parent categories */}
          {hasChildren && (
            <button
              onClick={toggleExpanded}
              className="btn btn-sm p-0 ms-2"
              style={{
                width: "16px",
                height: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                background: "none",
                color: "#6c757d",
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              >
                <path
                  d="M4.5 2.25L7.5 6L4.5 9.75"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Child categories */}
        {hasChildren && isExpanded && (
          <div className="mt-2">
            {category.children.map((childCategory) => (
              <div key={childCategory._id} className="mb-1">
                <CategoryItem category={childCategory} level={level + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Complex filter (kept, but now uses the same robust key collection)
  const filterProductsByCategory = async (categoryId) => {
    if (!categoryId) {
      setFilteredProducts(products);
      setVisibleProducts(10);
      return;
    }

    console.log(`=== DEBUGGING CATEGORY FILTER ===`);
    console.log(`Selected category ID: ${categoryId}`);
    console.log(`Total products available: ${products.length}`);
    console.log("Sample product structure:", products[0]);
    console.log("Available categories:", categories);

    const categoryTree = buildCategoryTree(categories);

    const findNode = (cats) => {
      for (const c of cats) {
        if (c._id === categoryId) return c;
        if (c.children && c.children.length) {
          const found = findNode(c.children);
          if (found) return found;
        }
      }
      return null;
    };

    const rootTree = categoryTree;
    const node = findNode(rootTree) || categoryById(categoryId);
    const categoryKeys = node
      ? collectCategoryKeysRecursively(node)
      : new Set([norm(categoryId), normLower(categoryId)]);

    const filtered = products.filter((product, index) => {
      const prodKeys = extractProductCategoryValues(product);
      for (const k of prodKeys) if (categoryKeys.has(k)) return true;
      return false;
    });

    console.log(
      `Found ${filtered.length} products in selected category and its subcategories`
    );
    console.log("=== END DEBUG ===");

    setFilteredProducts(filtered);
    setVisibleProducts(10);

    if (filtered.length === 0) {
      console.log("❌ NO PRODUCTS FOUND - Additional Debug Info:");
      console.log(
        "Selected category details:",
        categories.find((cat) => cat._id === categoryId)
      );

      const uniqueCategoryFields = new Set();
      products.forEach((product) => {
        Object.keys(product).forEach((key) => {
          if (key.toLowerCase().includes("cat")) {
            uniqueCategoryFields.add(key);
          }
        });
      });
      console.log(
        "Available category-related fields in products:",
        Array.from(uniqueCategoryFields)
      );

      console.log("Sample category values from products:");
      products.slice(0, 5).forEach((product, index) => {
        const categoryInfo = {};
        Object.keys(product).forEach((key) => {
          if (key.toLowerCase().includes("cat")) {
            categoryInfo[key] = product[key];
          }
        });
        console.log(`Product ${index + 1}:`, categoryInfo);
      });
    }
  };

  // Function to clear filters
  const clearFilters = () => {
    setSelectedCategory(null);
    setFilteredProducts(products);
    setIsSearchMode(false);
    setSearchResults([]);
    setVisibleProducts(10);
    navigate("/shop"); // Clear URL parameters
    handleClose(); // Close mobile menu when clearing filters
  };

  // Function to sort products based on selected option
  const sortProducts = (products, sortOption) => {
    const sortedProducts = [...products];

    switch (sortOption) {
      case "latest":
        // Sort by creation date (assuming createdAt field exists) or by _id as fallback
        return sortedProducts.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          }
          // Fallback to sorting by _id if no createdAt field
          return b._id?.localeCompare(a._id) || 0;
        });

      case "high":
        // Sort by price - high to low
        return sortedProducts.sort((a, b) => {
          const priceA = parseFloat(a.price) || 0;
          const priceB = parseFloat(b.price) || 0;
          return priceB - priceA;
        });

      case "low":
        // Sort by price - low to high
        return sortedProducts.sort((a, b) => {
          const priceA = parseFloat(a.price) || 0;
          const priceB = parseFloat(b.price) || 0;
          return priceA - priceB;
        });

      case "name":
        // Sort alphabetically by title/name
        return sortedProducts.sort((a, b) => {
          const nameA = (a.Title || a.name || "").toLowerCase();
          const nameB = (b.Title || b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });

      default:
        return sortedProducts;
    }
  };

  // Handle sort change
  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setVisibleProducts(10); // Reset visible products when sorting changes
  };

  // Toggle between complex and simple filtering for testing (kept but simple path already robust)
  const testSimpleFiltering = true;

  useEffect(() => {
    const getCategories = async () => {
      try {
        const catResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE}category/getCategories`
        );
        if (catResponse.data) {
          setCategories(catResponse.data.categories);
          console.log("Fetched categories:", catResponse.data.categories);
        } else {
          console.log("No categories found");
        }
      } catch (error) {
        console.log("Error fetching categories:", error);
      }
    };
    getCategories();
  }, []);

  useEffect(() => {
    const getProducts = async () => {
      try {
        const catResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE}product/getVisible`
        );

        if (catResponse.data) {
          const productsData = catResponse.data.products || [];
          setProducts(productsData);
          setFilteredProducts(productsData); // Initialize filtered products
          console.log("Fetched products:", productsData);
        } else {
          console.log("No products found");
        }
      } catch (error) {
        console.log("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    getProducts();
  }, []);

  // Add this useEffect after your existing useEffects to handle URL category parameter
  useEffect(() => {
    // Get category from URL parameters
    const searchParams = new URLSearchParams(location.search);
    const categoryParam = searchParams.get("category");

    if (categoryParam && categories.length > 0) {
      console.log("Category from URL:", categoryParam);

      // Find the category that matches the URL parameter
      // This could match by name, slug, or any other identifier
      const matchingCategory = categories.find(
        (cat) =>
          cat.Name?.toLowerCase() === categoryParam.toLowerCase() ||
          cat.slug?.toLowerCase() === categoryParam.toLowerCase() ||
          cat._id === categoryParam ||
          cat.code?.toLowerCase() === categoryParam.toLowerCase()
      );

      if (matchingCategory) {
        console.log("Found matching category:", matchingCategory);
        setSelectedCategory(matchingCategory._id);

        // Apply the category filter
        filterProductsByCategory(matchingCategory._id);

        // Expand parent categories if this is a child category
        const findParentCategories = (categoryId) => {
          const category = categories.find((cat) => cat._id === categoryId);
          if (category && category.parentId) {
            const newExpanded = new Set(expandedCategories);
            newExpanded.add(category.parentId);
            setExpandedCategories(newExpanded);
            // Recursively expand parent categories
            findParentCategories(category.parentId);
          }
        };
        findParentCategories(matchingCategory._id);
      } else {
        console.log("No matching category found for:", categoryParam);
        // Optionally, you could show a message or redirect
      }
    } else if (!categoryParam) {
      // If no category parameter, ensure we're showing all products
      setSelectedCategory(null);
      setFilteredProducts(products);
    }
  }, [location.search, categories, products]); // Dependencies include URL search and categories

  console.log(products);
  console.log(productMore);

  const [show, setShow] = useState(false);
  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);
  const [loading, setLoading] = useState(true);

  // Use appropriate products for display based on mode and apply sorting
  const displayProducts = isSearchMode ? searchResults : filteredProducts;
  const sortedProducts = sortProducts(displayProducts, sortBy);
  const totalProducts = sortedProducts.length;
  const currentProducts = sortedProducts.slice(0, visibleProducts);

  // Handle Show More functionality
  const handleShowMore = () => {
    setVisibleProducts((prev) => prev + productsPerPage);
  };

  const hasMoreProducts = visibleProducts < totalProducts;

  // Function to handle product click
  const handleProductClick = (productId, isOutOfStock) => {
    // Prevent navigation if product is out of stock
    if (isOutOfStock) {
      console.log("Product is out of stock, navigation prevented");
      return;
    }

    console.log("Clicked product ID:", productId);

    // Set in context
    setProductID(productId);

    // Navigate to product page with ID in URL
    navigate(`/shop/product/${productId}`);
  };

  // Helper function to check if product is out of stock
  const isOutOfStock = (product) => {
    return (
      product.quantity === 0 || product.quantity === "0" || !product.quantity
    );
  };

  // Build category tree for rendering
  const categoryTree = buildCategoryTree(categories);

  // Get display title based on current mode
  const getDisplayTitle = () => {
    if (isSearchMode && searchQuery) {
      return `Search Results for "${searchQuery}"`;
    } else if (selectedCategory) {
      return (
        categories.find((cat) => cat._id === selectedCategory)?.Name ||
        "Products"
      );
    } else {
      return "All Products";
    }
  };

  return (
    <section className={shopStyles.shopcontainer}>
      <Container fluid className="py-lg-5 py-3">
        <Row>
          <Col lg={12} className="border-top border-tertiary py-lg-4 py-3">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb" style={{ fontSize: "14px" }}>
                <li className="breadcrumb-item me-2">
                  <Link to="/" className="text-decoration-none text-muted">
                    Home
                  </Link>
                </li>
                <span className="me-2">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.53073 2.46937L11.5307 7.46937C11.6007 7.53905 11.6561 7.62184 11.694 7.71301C11.7318 7.80417 11.7513 7.90191 11.7513 8.00062C11.7513 8.09933 11.7318 8.19707 11.694 8.28824C11.6561 8.3794 11.6007 8.46219 11.5307 8.53187L6.53073 13.5319C6.38984 13.6728 6.19874 13.7519 5.99948 13.7519C5.80023 13.7519 5.60913 13.6728 5.46823 13.5319C5.32734 13.391 5.24818 13.1999 5.24818 13.0006C5.24818 12.8014 5.32734 12.6103 5.46823 12.4694L9.93761 8L5.46761 3.53062C5.32671 3.38973 5.24756 3.19863 5.24756 2.99937C5.24756 2.80011 5.32671 2.60902 5.46761 2.46812C5.60851 2.32723 5.7996 2.24807 5.99886 2.24807C6.19812 2.24807 6.38921 2.32723 6.53011 2.46812L6.53073 2.46937Z"
                      fill="black"
                      fillOpacity="0.6"
                    />
                  </svg>
                </span>
                {crumbs.length > 0 && crumbs}
              </ol>
            </nav>
          </Col>
          <Col lg={3} md={5} className="d-lg-block d-md-block d-none">
            <div className={shopStyles.filterscontainer}>
              <div className="d-flex justify-content-between border-bottom border-tertiary pb-3">
                <h5 className="fw-semibold">Filters</h5>
                <span>
                  <svg
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.125 12.125V20.75C13.125 21.0484 13.0065 21.3345 12.7955 21.5455C12.5845 21.7565 12.2984 21.875 12 21.875C11.7016 21.875 11.4155 21.7565 11.2045 21.5455C10.9935 21.3345 10.875 21.0484 10.875 20.75V12.125C10.875 11.8266 10.9935 11.5405 11.2045 11.3295C11.4155 11.1185 11.7016 11 12 11C12.2984 11 12.5845 11.1185 12.7955 11.3295C13.0065 11.5405 13.125 11.8266 13.125 12.125ZM18.75 18.5C18.4516 18.5 18.1655 18.6185 17.9545 18.8295C17.7435 19.0405 17.625 19.3266 17.625 19.625V20.75C17.625 21.0484 17.7435 21.3345 17.9545 21.5455C18.1655 21.7565 18.4516 21.875 18.75 21.875C19.0484 21.875 19.3345 21.7565 19.5455 21.5455C19.7565 21.3345 19.875 21.0484 19.875 20.75V19.625C19.875 19.3266 19.7565 19.0405 19.5455 18.8295C19.3345 18.6185 19.0484 18.5 18.75 18.5ZM21 14.75H19.875V4.25C19.875 3.95163 19.7565 3.66548 19.5455 3.4545C19.3345 3.24353 19.0484 3.125 18.75 3.125C18.4516 3.125 18.1655 3.24353 17.9545 3.4545C17.7435 3.66548 17.625 3.95163 17.625 4.25V14.75H16.5C16.2016 14.75 15.9155 14.8685 15.7045 15.0795C15.4935 15.2905 15.375 15.5766 15.375 15.875C15.375 16.1734 15.4935 16.4595 15.7045 16.6705C15.9155 16.8815 16.2016 17 16.5 17H21C21.2984 17 21.5845 16.8815 21.7955 16.6705C22.0065 16.4595 22.125 16.1734 22.125 15.875C22.125 15.5766 22.0065 15.2905 21.7955 15.0795C21.5845 14.8685 21.2984 14.75 21 14.75ZM5.25 15.5C4.95163 15.5 4.66548 15.6185 4.4545 15.8295C4.24353 16.0405 4.125 16.3266 4.125 16.625V20.75C4.125 21.0484 4.24353 21.3345 4.4545 21.5455C4.66548 21.7565 4.95163 21.875 5.25 21.875C5.54837 21.875 5.83452 21.7565 6.0455 21.5455C6.25647 21.3345 6.375 21.0484 6.375 20.75V16.625C6.375 16.3266 6.25647 16.0405 6.0455 15.8295C5.83452 15.6185 5.54837 15.5 5.25 15.5ZM7.5 11.75H6.375V4.25C6.375 3.95163 6.25647 3.66548 6.0455 3.4545C5.83452 3.24353 5.54837 3.125 5.25 3.125C4.95163 3.125 4.66548 3.24353 4.4545 3.4545C4.24353 3.66548 4.125 3.95163 4.125 4.25V11.75H3C2.70163 11.75 2.41548 11.8685 2.2045 12.0795C1.99353 12.2905 1.875 12.5766 1.875 12.875C1.875 13.1734 1.99353 13.4595 2.2045 13.6705C2.41548 13.8815 2.70163 14 3 14H7.5C7.79837 14 8.08452 13.8815 8.2955 13.6705C8.50647 13.4595 8.625 13.1734 8.625 12.875C8.625 12.5766 8.50647 12.2905 8.2955 12.0795C8.08452 11.8685 7.79837 11.75 7.5 11.75ZM14.25 7.25H13.125V4.25C13.125 3.95163 13.0065 3.66548 12.7955 3.4545C12.5845 3.24353 12.2984 3.125 12 3.125C11.7016 3.125 11.4155 3.24353 11.2045 3.4545C10.9935 3.66548 10.875 3.95163 10.875 4.25V7.25H9.75C9.45163 7.25 9.16548 7.36853 8.9545 7.5795C8.74353 7.79048 8.625 8.07663 8.625 8.375C8.625 8.67337 8.74353 8.95952 8.9545 9.1705C9.16548 9.38147 9.45163 9.5 9.75 9.5H14.25C14.5484 9.5 14.8345 9.38147 15.0455 9.1705C15.2565 8.95952 15.375 8.67337 15.375 8.375C15.375 8.07663 15.2565 7.79048 15.0455 7.5795C14.8345 7.36853 14.5484 7.25 14.25 7.25Z"
                      fill="black"
                      fillOpacity="0.4"
                    />
                  </svg>
                </span>
              </div>
              <div className="pt-4 pb-3 border-bottom border-tertiary">
                <Accordion defaultActiveKey="1" id="category-options">
                  <Accordion.Item eventKey="1" className="border-0">
                    <Accordion.Header>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <h5 className="fw-semibold mb-0">Category</h5>
                        {selectedCategory && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              clearFilters();
                            }}
                            className="btn btn-sm text-primary p-0 me-2"
                            style={{ fontSize: "12px" }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </Accordion.Header>
                    <Accordion.Body className="py-3 px-0">
                      <div
                        className={shopStyles.sizebtncontainer}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        {/* Add "All Categories" option */}
                        <div className="mb-3">
                          <span
                            onClick={clearFilters}
                            className={`${
                              !selectedCategory
                                ? "fw-bold text-primary"
                                : "text-dark"
                            }`}
                            style={{
                              cursor: "pointer",
                              fontSize: "14px",
                              lineHeight: "1.4",
                              display: "block",
                              padding: "4px 0",
                            }}
                          >
                            All Categories
                          </span>
                        </div>

                        {/* Render nested categories as expandable text */}
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          {categoryTree.map((category) => (
                            <div key={category._id} className="w-100">
                              <CategoryItem category={category} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </div>
            </div>
          </Col>
          <Col lg={9} md={7}>
            <div className="mb-4 d-flex align-items-lg-center justify-content-between flex-lg-row flex-column">
              <div className="d-flex justify-content-between">
                <h2 className="fw-semibold">
                  {selectedCategory
                    ? categories.find((cat) => cat._id === selectedCategory)
                        ?.Name || "Products"
                    : "All Products"}
                </h2>
                <div
                  className={`${shopStyles.filtermenuiconbg} d-lg-none d-md-none d-flex`}
                >
                  <svg
                    onClick={handleShow}
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.75 7.75V13.5C8.75 13.6989 8.67098 13.8897 8.53033 14.0303C8.38968 14.171 8.19891 14.25 8 14.25C7.80109 14.25 7.61032 14.171 7.46967 14.0303C7.32902 13.8897 7.25 13.6989 7.25 13.5V7.75C7.25 7.55109 7.32902 7.36032 7.46967 7.21967C7.61032 7.07902 7.80109 7 8 7C8.19891 7 8.38968 7.07902 8.53033 7.21967C8.67098 7.36032 8.75 7.55109 8.75 7.75ZM12.5 12C12.3011 12 12.1103 12.079 11.9697 12.2197C11.829 12.3603 11.75 12.5511 11.75 12.75V13.5C11.75 13.6989 11.829 13.8897 11.9697 14.0303C12.1103 14.171 12.3011 14.25 12.5 14.25C12.6989 14.25 12.8897 14.171 13.0303 14.0303C13.171 13.8897 13.25 13.6989 13.25 13.5V12.75C13.25 12.5511 13.171 12.3603 13.0303 12.2197C12.8897 12.079 12.6989 12 12.5 12ZM14 9.5H13.25V2.5C13.25 2.30109 13.171 2.11032 13.0303 1.96967C12.8897 1.82902 12.6989 1.75 12.5 1.75C12.3011 1.75 12.1103 1.82902 11.9697 1.96967C11.829 2.11032 11.75 2.30109 11.75 2.5V9.5H11C10.8011 9.5 10.6103 9.57902 10.4697 9.71967C10.329 9.86032 10.25 10.0511 10.25 10.25C10.25 10.4489 10.329 10.6397 10.4697 10.7803C10.6103 10.921 10.8011 11 11 11H14C14.1989 11 14.3897 10.921 14.5303 10.7803C14.671 10.6397 14.75 10.4489 14.75 10.25C14.75 10.0511 14.671 9.86032 14.5303 9.71967C14.3897 9.57902 14.1989 9.5 14 9.5ZM3.5 10C3.30109 10 3.11032 10.079 2.96967 10.2197C2.82902 10.3603 2.75 10.5511 2.75 10.75V13.5C2.75 13.6989 2.82902 13.8897 2.96967 14.0303C3.11032 14.171 3.30109 14.25 3.5 14.25C3.69891 14.25 3.88968 14.171 4.03033 14.0303C4.17098 13.8897 4.25 13.6989 4.25 13.5V10.75C4.25 10.5511 4.17098 10.3603 4.03033 10.2197C3.88968 10.079 3.69891 10 3.5 10ZM5 7.5H4.25V2.5C4.25 2.30109 4.17098 2.11032 4.03033 1.96967C3.88968 1.82902 3.69891 1.75 3.5 1.75C3.30109 1.75 3.11032 1.82902 2.96967 1.96967C2.82902 2.11032 2.75 2.30109 2.75 2.5V7.5H2C1.80109 7.5 1.61032 7.57902 1.46967 7.71967C1.32902 7.86032 1.25 8.05109 1.25 8.25C1.25 8.44891 1.32902 8.63968 1.46967 8.78033C1.61032 8.92098 1.80109 9 2 9H5C5.19891 9 5.38968 8.92098 5.53033 8.78033C5.67098 8.63968 5.75 8.44891 5.75 8.25C5.75 8.05109 5.67098 7.86032 5.53033 7.71967C5.38968 7.57902 5.19891 7.5 5 7.5ZM9.5 4.5H8.75V2.5C8.75 2.30109 8.67098 2.11032 8.53033 1.96967C8.38968 1.82902 8.19891 1.75 8 1.75C7.80109 1.75 7.61032 1.82902 7.46967 1.96967C7.32902 2.11032 7.25 2.30109 7.25 2.5V4.5H6.5C6.30109 4.5 6.11032 4.57902 5.96967 4.71967C5.82902 4.86032 5.75 5.05109 5.75 5.25C5.75 5.44891 5.82902 5.63968 5.96967 5.78033C6.11032 5.92098 6.30109 6 6.5 6H9.5C9.69891 6 9.88968 5.92098 10.0303 5.78033C10.171 5.63968 10.25 5.44891 10.25 5.25C10.25 5.05109 10.171 4.86032 10.0303 4.71967C9.88968 4.57902 9.69891 4.5 9.5 4.5Z"
                      fill="black"
                    />
                  </svg>
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-between flex-wrap">
                <span
                  style={{ fontSize: "14px", color: "rgba(0,0,0,0.6)" }}
                  className="me-3"
                >
                  Showing {totalProducts > 0 ? 1 : 0}–
                  {Math.min(visibleProducts, totalProducts)} of {totalProducts}{" "}
                  Products
                  {selectedCategory && (
                    <span
                      className="d-block"
                      style={{ fontSize: "12px", fontStyle: "italic" }}
                    >
                      (Including all subcategories)
                    </span>
                  )}
                </span>

                <div
                  className="d-flex align-items-center"
                  style={{ fontSize: "13px", gap: "6px" }}
                >
                  <span>Sort by:</span>
                  <Form.Select
                    aria-label="Sort products"
                    className="border-0"
                    style={{ fontSize: "13px", width: "150px" }}
                    value={sortBy}
                    onChange={handleSortChange}
                  >
                    <option value="latest">Latest</option>
                    <option value="name">Name (A-Z)</option>
                    <option value="high">Price (High-Low)</option>
                    <option value="low">Price (Low-High)</option>
                  </Form.Select>
                </div>
              </div>
            </div>
            <Row className="border-bottom border-tertiary pb-4 justify-content-start">
              {loading && (
                <div className="text-center">
                  <Spinner animation="grow" />
                </div>
              )}
              {!loading &&
              Array.isArray(currentProducts) &&
              currentProducts.length > 0 ? (
                currentProducts.map((item) => {
                  const productOutOfStock = isOutOfStock(item);
                  // Check if product has sizes
                  const hasSizes =
                    item.size &&
                    Array.isArray(item.size) &&
                    item.size.length > 0;

                  return (
                    <Col
                      lg={4}
                      md={6}
                      xs={6}
                      key={item._id || item.id}
                      className={shopStyles.prodresults}
                      onClick={() =>
                        handleProductClick(item._id, productOutOfStock)
                      }
                      style={{
                        cursor: productOutOfStock ? "not-allowed" : "pointer",
                        margin: "0 0 20px 0",
                      }}
                    >
                      <Card className="border-0">
                        <Card.Body className="p-0">
                          <div
                            className="mb-3"
                            style={{ position: "relative" }}
                          >
                            <Image
                              src={getProductImage(item.image_link)}
                              fluid
                              className={shopStyles.productimg}
                              style={{
                                filter: productOutOfStock
                                  ? "grayscale(100%)"
                                  : "none",
                                opacity: productOutOfStock ? 0.5 : 1,
                                transition: "all 0.3s ease",
                              }}
                              onError={(e) => {
                                e.target.src =
                                  "https://via.placeholder.com/300";
                              }}
                            />
                            {productOutOfStock && (
                              <div
                                style={{
                                  position: "absolute",
                                  bottom: "0",
                                  left: "0",
                                  right: "0",
                                  backgroundColor: "rgba(0, 0, 0, 0.85)",
                                  color: "white",
                                  padding: "6px 8px",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  textAlign: "center",
                                  pointerEvents: "none",
                                }}
                              >
                                Out of Stock
                              </div>
                            )}
                          </div>

                          <Card.Title
                            className="fw-semibold text-capitalize"
                            style={{ fontSize: "14px", marginBottom: "8px" }}
                          >
                            {item.Title}
                          </Card.Title>

                          {/* Size Display */}
                          {hasSizes && (
                            <div className="mb-2">
                              <div
                                className="d-flex flex-wrap gap-1"
                                style={{ marginBottom: "8px" }}
                              >
                                {item.size.slice(0, 4).map((size, index) => (
                                  <span
                                    key={index}
                                    style={{
                                      display: "inline-block",
                                      padding: "2px 8px",
                                      fontSize: "11px",
                                      fontWeight: "500",
                                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                                      border: "1px solid rgba(0, 0, 0, 0.1)",
                                      borderRadius: "4px",
                                      color: "#333",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {size}
                                  </span>
                                ))}
                                {item.size.length > 4 && (
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "2px 8px",
                                      fontSize: "11px",
                                      fontWeight: "500",
                                      color: "#666",
                                    }}
                                  >
                                    +{item.size.length - 4} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="d-flex align-items-center pb-4">
                            <div>
                              <span
                                className={`${shopStyles.sliderprice} me-2 fw-semibold`}
                              >
                                ${item.price?.toFixed(2) || "0.00"}
                              </span>
                              {productOutOfStock && (
                                <span
                                  className="text-danger"
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "500",
                                  }}
                                >
                                  (Out of Stock)
                                </span>
                              )}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })
              ) : !loading ? (
                <div className="text-center py-5">
                  <p className="text-muted">
                    {selectedCategory
                      ? "No products found in this category."
                      : "No products found."}
                  </p>
                  {selectedCategory && (
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={clearFilters}
                    >
                      View All Products
                    </button>
                  )}
                </div>
              ) : null}
            </Row>

            {/* Show More Button */}
            {hasMoreProducts && !loading && (
              <div className="d-flex justify-content-center py-4">
                <button
                  className="btn btn-outline-primary px-4 py-2"
                  onClick={handleShowMore}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                    minWidth: "120px",
                  }}
                >
                  Show More ({totalProducts - visibleProducts} remaining)
                </button>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {/* Mobile Filter Offcanvas */}
      <Offcanvas
        show={show}
        onHide={handleClose}
        placement="bottom"
        className={shopStyles.filtermenu}
        style={{ height: "70vh" }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <h5 className="fw-semibold mb-0">Filters</h5>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ overflowY: "auto" }}>
          <div className="pt-2 pb-3 border-bottom border-tertiary">
            <Accordion defaultActiveKey="1" id="mobile-category-options">
              <Accordion.Item eventKey="1" className="border-0">
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <h6 className="fw-semibold mb-0">Category</h6>
                    {selectedCategory && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          clearFilters();
                        }}
                        className="btn btn-sm text-primary p-0 me-2"
                        style={{ fontSize: "12px" }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </Accordion.Header>
                <Accordion.Body className="py-3 px-0">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    {/* Add "All Categories" option */}
                    <div className="mb-3">
                      <span
                        onClick={clearFilters}
                        className={`${
                          !selectedCategory
                            ? "fw-bold text-primary"
                            : "text-dark"
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "14px",
                          lineHeight: "1.4",
                          display: "block",
                          padding: "4px 0",
                        }}
                      >
                        All Categories
                      </span>
                    </div>

                    {/* Render nested categories as expandable text */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {categoryTree.map((category) => (
                        <div key={category._id} className="w-100">
                          <CategoryItem category={category} />
                        </div>
                      ))}
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </section>
  );
};

export default Shop;
